import styled, { css } from 'styled-components';
import { colors, gradients, spacing } from '../../constants/theme';

// Base button styles
const baseButtonStyles = css`
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  text-decoration: none;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
`;

export const BaseButton = styled.button<{ 
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}>`
  ${baseButtonStyles}
  
  ${props => {
    switch (props.size) {
      case 'sm':
        return css`
          padding: ${spacing.sm} ${spacing.md};
          font-size: 14px;
        `;
      case 'lg':
        return css`
          padding: ${spacing.lg} ${spacing.xl};
          font-size: 18px;
          min-height: 56px;
        `;
      default:
        return css`
          padding: ${spacing.md} ${spacing.lg};
          font-size: 16px;
          min-height: 44px;
        `;
    }
  }}

  ${props => props.fullWidth && css`
    width: 100%;
  `}
`;

export const PrimaryButton = styled(BaseButton)`
  background: ${gradients.primary};
  color: white;

  &:hover:not(:disabled) {
    box-shadow: 0 8px 25px ${colors.primary.main}40;
  }
`;

export const SecondaryButton = styled(BaseButton)`
  background: ${colors.background.tertiary};
  border: 1px solid ${colors.background.tertiary};
  color: ${colors.text.primary};

  &:hover:not(:disabled) {
    background: ${colors.primary.main}20;
    border-color: ${colors.primary.main};
    color: ${colors.primary.light};
  }
`;

export const DangerButton = styled(BaseButton)`
  background: transparent;
  border: 1px solid ${colors.background.tertiary};
  color: ${colors.text.secondary};

  &:hover:not(:disabled) {
    background: ${colors.error}20;
    border-color: ${colors.error};
    color: ${colors.error};
  }
`;

export const IconButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  ${baseButtonStyles}
  width: 40px;
  height: 40px;
  border-radius: 8px;
  padding: 0;
  
  ${props => props.variant === 'primary' ? css`
    background: ${colors.primary.main};
    color: ${colors.text.primary};
    
    &:hover:not(:disabled) {
      background: ${colors.primary.dark};
    }
  ` : css`
    background: ${colors.background.tertiary};
    color: ${colors.text.primary};
    border: 1px solid ${colors.background.tertiary};
    
    &:hover:not(:disabled) {
      background: ${colors.primary.main}20;
      border-color: ${colors.primary.main};
    }
  `}
`;

export const TabButton = styled.button<{ $active: boolean }>`
  ${baseButtonStyles}
  flex: 1;
  padding: ${spacing.md} ${spacing.lg};
  border-radius: 8px;
  font-size: 14px;
  background: ${props => props.$active ? gradients.primary : 'transparent'};
  color: ${props => props.$active ? colors.text.primary : colors.text.secondary};

  &:hover {
    background: ${props => props.$active ? gradients.primary : `${colors.background.tertiary}60`};
    color: ${colors.text.primary};
  }
`;
